"use client";
import { useState, useEffect } from "react";
import "./Dashboard.css";
import { Navbar } from "./Navbar";
import Link from "next/link";
import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import AddResumeIcon from "public/assets/dashboardIcons/AddResumeIcon.svg";
import PlusIcon from "public/assets/dashboardIcons/PlusIcon.svg";
import AutofillBlackIcon from "public/assets/AutofillBlackIcon.svg";
import DashboardResume from "./DashboardResume";
import type { Resume } from "lib/redux/types";
import {
  useAppDispatch,
  useAppSelector,
  useSaveStateToLocalStorageOnChange,
  useSetInitialStore,
} from "lib/redux/hooks";
import { resetResume } from "lib/redux/resumeSlice";
import {
  setImportComponentOpen,
  toggleReset,
  setFormHeadingToDefault,
  selectFormToHeading,
  ShowForm,
} from "lib/redux/settingsSlice";
import { getUserProfile } from "api/userApi";
import { initialSettings, type Settings } from "lib/redux/settingsSlice";
import {
  getAllResumes,
  deleteResume,
  createResume,
  changeResumeVisibility,
  getResumeStats,
} from "api/resumeApi";
import { useRouter } from "next/navigation";
import { ResumePublishOptions } from "../components/ResumePublishOptions";
import { getTimeAgo, getDuration } from "lib/utils/resumeStats";
import { getFunkyName } from "lib/utils/getFunkyName";
import { isTokenExpired } from "lib/utils/verifyJwt";
import { selectIsPremiumUser, setAccessToken, setIsPremiumUser } from "lib/redux/uiSlice";
import { getSubscriptionInfo } from "api/subscription/subscriptionInfo";
import {
  FontPreloader,
  useUniqueFontFamilies,
} from "components/fonts/FontPreloader";
import PremiumBanner from "components/PremiumBanner";

export interface ResumeStats {
  resumeId: number;
  totalViews: number;
  totalDownloads: number;
  uniqueViews: number;
  topSessions: {
    duration: number;
    createdOn: string;
    isDownloaded: boolean;
    lastAccessed: string;
    location: {
      city: string;
      country: string;
      state: string;
    };
    deviceId: string;
  }[];
}

const Dashboard = () => {
  useSetInitialStore();
  useSaveStateToLocalStorageOnChange();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [allUserResumes, setAllUserResumes] = useState<
    {
      resume: Resume;
      settings: Settings;
    }[]
  >([]);
  const [allResumeStats, setAllResumeStats] = useState<ResumeStats[]>([]);
  const [isResumeSelected, setIsResumeSelected] = useState<number | null>(null);
  const formToHeading = useAppSelector(selectFormToHeading);
  const allHeadings = Object.keys(formToHeading) as ShowForm[];
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [openShareResumeModal, setOpenShareResumeModal] = useState(false);
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isPremiumUser = useAppSelector(selectIsPremiumUser);
  console.log("isPremiumUser:", isPremiumUser);

  const findCurrentResumeStats = (id: number) => {
    return allResumeStats?.find((resumeStats) => resumeStats.resumeId === id);
  };
  const currentResumeStats = findCurrentResumeStats(isResumeSelected!);

  // Get unique font families from all resumes to preload only necessary fonts
  const uniqueFontFamilies = useUniqueFontFamilies(allUserResumes);

  const fetchData = async () => {
    try {
      // Parallelize API calls for faster loading
      const [userProfile, resumes] = await Promise.all([
        getUserProfile(),
        getAllResumes(),
      ]);

      setCurrentUser(userProfile);

      // Combine resumes with their initial settings
      const resumesWithSettings = resumes.map((resume) => ({
        resume,
        settings: {
          ...initialSettings,
          fontFamily: resume.fontFamily,
          fontSize: resume.fontSize,
          themeColor: resume.themeColor,
          documentSize: resume.documentSize,
          formToShow: resume.formToShow,
          formToHeading: resume.formToHeading,
          formsOrder: resume.formsOrder as ShowForm[],
          leftRightFormOrder: {
            left: resume.leftRightFormOrder?.left as ShowForm[],
            right: resume.leftRightFormOrder?.right as ShowForm[],
          },
        },
      }));

      setAllUserResumes(resumesWithSettings);
      handleResumeSelect(resumesWithSettings[0]?.resume.id!);
      setIsLoading(false);

      // Load stats in parallel, don't block initial render
      handleResumeStats();
    } catch (error: any) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const access_token = localStorage.getItem("access-token");
    if (!access_token || isTokenExpired(access_token)) {
      localStorage.removeItem("access-token");
      dispatch(setAccessToken(null));
      router.push("/");
    } else {
      // Ensure Redux knows about premium status as early as possible
      (async () => {
        try {
          const tokenCount = await getSubscriptionInfo();
          // debug log to help trace why isPremiumUser might be false
          // console.debug("subscriptionInfo on Dashboard mount:", tokenCount);
          dispatch(setIsPremiumUser(Boolean(tokenCount?.premium)));
        } catch (e) {
          console.log("failed to fetch subscription info on Dashboard:", e);
        }
      })();
      fetchData();
    }
  }, []);

  const handleDeleteResume = async (resumeId: number) => {
    await deleteResume(resumeId);
    const newResumes = allUserResumes.filter(
      (resumesWithSettings) => resumesWithSettings.resume.id !== resumeId
    );
    setAllUserResumes(newResumes);
  };

  const handleDuplicateResume = async (resumeId: number) => {
    const selectedResume = allUserResumes.find(
      (resumesWithSettings) => resumesWithSettings.resume.id === resumeId
    );
    if (selectedResume) {
      const newResume = await createResume(selectedResume.resume);
      setAllUserResumes([
        ...allUserResumes,
        { resume: newResume, settings: selectedResume.settings },
      ]);
    }
  };

  const handleChangeResumeVisibility = async (
    resumeId: number,
    visibility: boolean
  ) => {
    try {
      // Call API to change visibility
      await changeResumeVisibility(resumeId, {
        published: visibility,
      });
      const newResumes = allUserResumes.map((resumesWithSettings) => {
        if (resumesWithSettings.resume.id === resumeId) {
          return {
            ...resumesWithSettings,
            resume: {
              ...resumesWithSettings.resume,
              published: visibility,
            },
          };
        }
        return resumesWithSettings;
      });

      // Update the state
      setAllUserResumes(newResumes);
    } catch (error) {
      console.error("Error updating resume visibility:", error);
    }
  };

  const handleChangeResumeName = async (resumeId: number, name: string) => {
    try {
      // Call API to change visibility
      await changeResumeVisibility(resumeId, {
        displayName: name,
      });
      const newResumes = allUserResumes.map((resumesWithSettings) => {
        if (resumesWithSettings.resume.id === resumeId) {
          return {
            ...resumesWithSettings,
            resume: {
              ...resumesWithSettings.resume,
              displayName: name,
            },
          };
        }
        return resumesWithSettings;
      });

      setAllUserResumes(newResumes);
    } catch (error) {
      console.error("Error updating resume visibility:", error);
    }
  };

  const handleResumeStats = async () => {
    try {
      const allResumeStats = await getResumeStats(8);
      setAllResumeStats(allResumeStats);
    } catch (error) {
      console.error("Error fetching resume stats:", error);
    }
  };

  // Function to update selected resume
  const handleResumeSelect = (resumeId: number) => {
    setIsResumeSelected(resumeId);
  };

  // const toggleImportComponentState = () => {
  //   dispatch(toggleImportComponent()); // Dispatching the action to toggle the state
  // };

  const handleCreateNewResume = () => {
    dispatch(toggleReset());
    dispatch(resetResume());
    dispatch(setFormHeadingToDefault({ field: allHeadings }));
    router.push("/resume-builder");
  };

  const handleResumeImport = () => {
    dispatch(setImportComponentOpen(true));
    router.push("/resume-builder");
  };

  return (
    <>
      {/* Preload only fonts used by resumes on this dashboard */}
      <FontPreloader fontFamilies={uniqueFontFamilies} />
      <section className="h-screen overflow-hidden bg-[#F1F1F1] px-4 md:px-8 lg:flex lg:flex-col">
        <Navbar />
        <div className="flex flex-row justify-between items-center font-montserrat md:my-8 xl:my-4">
          <div>
            <div className="text-xl font-semibold md:text-[48px] lg:text-[26px] xl:text-[32px] 2xl:text-[38.4px]">
              Hi, {currentUser?.name}!
            </div>
            <div className="-mt-1 text-base font-medium text-[#A0A0A0] md:mt-5 md:text-[38.4px] lg:text-[20px] lg:mt-3 xl:mt-2 xl:text-xl 2xl:text-[24px]">
              <span className="hidden lg:block">Your Resumes</span>
              <span className="lg:hidden">Looking for a new resume ?</span>
            </div>
          </div>
          {(!isPremiumUser) && (
            <div className="hidden lg:flex">
              <Link href="/premium-subscription">
                <PremiumBanner />
              </Link>
            </div>
          )}
        </div>
        <div className="mx-auto my-5 flex w-full justify-between font-poppins text-[10px] md:text-2xl lg:hidden">
          <button
            className="btn-gradient rounded-[23.7px] px-4 py-2 md:px-[46.67px] md:py-[11.67px]"
            onClick={handleCreateNewResume}
          >
            Create New <ArrowLongRightIcon className="h-auto w-4 md:w-6" />
          </button>

          <button
            className="flex items-center gap-2 rounded-[23.7px] bg-white px-4 py-2 shadow-2xl md:px-[46.67px] md:py-[11.67px]"
            onClick={handleResumeImport}
          >
            Import Existing One
            <AutofillBlackIcon className="h-4 w-4" />
          </button>
        </div>
        <hr className="mb-5 bg-[#E4E4E4] lg:hidden" />
        {/* {Laptop and Above Screens} */}
        <div className="hidden h-[calc(100vh-220px)] lg:grid lg:flex-grow lg:grid-cols-10">
          <div className="col-span-6 flex max-h-full flex-wrap content-start justify-start overflow-y-auto pb-4 pr-4 scrollbar-thin lg:gap-4 xl:gap-7">
            {isLoading ? (
              // Loading skeleton
              <>
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-[20vw] w-[17vw] animate-pulse rounded-[21px] bg-gray-200"
                  ></div>
                ))}
              </>
            ) : (
              <>
                <div
                  className="flex h-[20vw] w-[17vw] cursor-pointer flex-col items-center justify-center gap-2 rounded-[21px] bg-white text-[13.27px] font-medium"
                  onClick={handleCreateNewResume}
                >
                  <PlusIcon />
                  <span className="text-gradient">Create New</span>
                </div>
                <div
                  className="flex h-[20vw] w-[17vw] cursor-pointer flex-col items-center justify-center gap-2 rounded-[21px] bg-white text-[13.27px] font-medium"
                  onClick={handleResumeImport}
                >
                  <AddResumeIcon />
                  <span className="text-gradient">Import Existing Resume</span>
                </div>
                {allUserResumes.map((userResume) => (
                  <DashboardResume
                    key={userResume.resume.id}
                    resume={userResume.resume}
                    isResumeSelected={isResumeSelected === userResume.resume.id}
                    handleResumeSelect={handleResumeSelect}
                    handleDeleteResume={handleDeleteResume}
                    handleDuplicateResume={handleDuplicateResume}
                    handleResumeShare={() => setOpenShareResumeModal(true)}
                    handleChangeResumeName={handleChangeResumeName}
                    setSelectedResume={setSelectedResume}
                    resumeSettings={userResume.settings}
                  />
                ))}
              </>
            )}
          </div>
          <div className="col-span-4 h-full overflow-y-auto font-montserrat scrollbar-thin">
            <h2 className="text-center text-xl font-medium text-[#2C2C2C]">
              Sharing Statics
            </h2>
            <p className="text-center text-[#2C2C2C]">
              {currentResumeStats?.topSessions[0]?.lastAccessed ? (
                <span>
                  Your Resume was last viewed{" "}
                  {getTimeAgo(currentResumeStats.topSessions[0].lastAccessed)}
                </span>
              ) : (
                <span>
                  Share your resume with your friends and get more views
                </span>
              )}
            </p>
            <div className="mt-4 flex items-center justify-around text-[32px]">
              <div className="flex flex-col items-center">
                {currentResumeStats?.totalViews}
                <span className="text-[12px] text-[#9E9E9E]">Views</span>
              </div>
              <div className="h-6 w-[1px] bg-[#CBCBCB]"></div>
              <div className="flex flex-col items-center">
                {currentResumeStats?.totalDownloads}
                <span className="text-[12px] text-[#9E9E9E]">Downloads</span>
              </div>
            </div>
            <div className="flex flex-col gap-5 px-12 pt-10">
              {currentResumeStats?.topSessions.map((resumeStats) => (
                <div
                  key={resumeStats.createdOn}
                  className="flex justify-between"
                >
                  <div>
                    <div className="font-semibold">
                      {getFunkyName(resumeStats.deviceId)}
                    </div>
                    <div className="text-xs text-[#9E9E9E]">
                      Viewed for {getDuration(resumeStats.duration)} <br />
                      {resumeStats.isDownloaded ? "Downloaded" : ""}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium">
                      {getTimeAgo(resumeStats.createdOn)}
                    </div>
                    <div className="text-xs text-[#9E9E9E]">
                      {resumeStats.location.city +
                        ", " +
                        resumeStats.location.state +
                        ", " +
                        resumeStats.location.country}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex h-[calc(100vh-270px)] flex-col gap-4 overflow-y-auto lg:hidden">
          {isLoading ? (
            // Mobile loading skeleton
            <>
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-32 animate-pulse rounded-lg bg-gray-200"
                ></div>
              ))}
            </>
          ) : (
            allUserResumes.map((userResume) => (
              <DashboardResume
                key={userResume.resume.id}
                resume={userResume.resume}
                isResumeSelected={isResumeSelected === userResume.resume.id}
                handleResumeSelect={handleResumeSelect}
                handleDeleteResume={handleDeleteResume}
                handleDuplicateResume={handleDuplicateResume}
                handleResumeShare={() => setOpenShareResumeModal(true)}
                handleChangeResumeName={handleChangeResumeName}
                setSelectedResume={setSelectedResume}
                resumeSettings={userResume.settings}
                resumeStats={findCurrentResumeStats(userResume.resume.id!)}
              />
            ))
          )}
        </div>

        {openShareResumeModal && (
          <ResumePublishOptions
            onClose={() => setOpenShareResumeModal(false)}
            resumeId={selectedResume?.id!}
            handleChangeResumeVisibility={handleChangeResumeVisibility}
            published={selectedResume?.published!}
          />
        )}
      </section>
    </>
  );
};

export default Dashboard;
